/** Small presentational primitives shared by every page. */

import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { money, moneyExact } from '../lib/format';

/**
 * A money figure as it should be read: floored to хил./млн./млрд. so a
 * column of them scans at a glance, with every published digit one hover
 * away in the title. The compact text is the reading; the title is the
 * evidence.
 */
export function Amount({ value, currency = 'BGN', className = 'amount' }: {
  value: number | null | undefined;
  currency?: string | null;
  className?: string;
}) {
  if (value == null) return <>—</>;
  return (
    <span className={className} title={moneyExact(value, currency)}>
      {money(value, currency)}
    </span>
  );
}

export type BadgeKind = 'company' | 'consortium' | 'person' | 'municipality'
  | 'party' | 'debt' | 'record';

export function Badge({ kind, children }: { kind: BadgeKind; children: ReactNode }) {
  return <span className={`badge badge-${kind}`}>{children}</span>;
}

export function Link({ href, children, className = 'rowlink' }: {
  href: string; children: ReactNode; className?: string;
}) {
  return <a className={className} href={href}>{children}</a>;
}

export function PageHead({ eyebrow, title, lede, actions }: {
  eyebrow?: string; title: ReactNode; lede?: ReactNode; actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h1 className="page-title">{title}</h1>
      {lede && <p className="page-lede">{lede}</p>}
      {actions && <div className="detail-actions" style={{ marginTop: '1.1rem' }}>{actions}</div>}
    </header>
  );
}

export function DetailHead({ eyebrow, title, meta, actions, compact }: {
  eyebrow: string;
  title: string;
  meta?: (readonly [string, ReactNode] | null | false)[];
  actions?: ReactNode;
  compact?: boolean;
}) {
  const rows = (meta ?? []).filter(Boolean) as (readonly [string, ReactNode])[];
  return (
    <header className="detail-head">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        {/* procurement titles run to whole paragraphs; at display size they
            push everything else off the screen */}
        <h1 className={compact ? 'detail-title detail-title-long' : 'detail-title'}>{title}</h1>
        {rows.length > 0 && (
          <dl className="detail-meta">
            {rows.map(([term, value]) => (
              <div key={term}><dt>{term}</dt><dd>{value}</dd></div>
            ))}
          </dl>
        )}
      </div>
      {actions && <div className="detail-actions">{actions}</div>}
    </header>
  );
}

export function SectionHead({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className="section-head">
      <h2 className="section-title">{title}</h2>
      {aside && <span className="muted">{aside}</span>}
    </div>
  );
}

/**
 * A headline figure, and where to go to see what it counts.
 *
 * With `href` the whole card is the control. A summary that reports 224
 * companies and gives no way to look at them asks the reader to take the
 * number on trust, which is the opposite of what a page of observations
 * is for.
 */
export function Stat({ value, label, href }: {
  value: ReactNode; label: string; href?: string | undefined;
}) {
  const body = (
    <>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </>
  );
  return href
    ? <a className="stat stat-link" href={href}>{body}</a>
    : <div className="stat">{body}</div>;
}

export function Fact({ value, label }: { value: ReactNode; label: string }) {
  return <div className="fact"><span className="fact-value">{value}</span><span className="fact-label">{label}</span></div>;
}

/**
 * How many, how much, and what that averages to.
 *
 * The average is the reason the pair exists: 600 contracts and 11.1bn
 * are each a fact you can read off a register, while 18.6m per contract
 * is the one that says what kind of buyer this is — and getting it
 * previously meant scrolling a list and dividing by hand.
 *
 * With nothing counted there is no average, and it shows as a dash. Zero
 * would be a different claim: that the contracts averaged nothing.
 */
export function FactPair({ count, countLabel, total, totalLabel, averageLabel, format }: Readonly<{
  count: number;
  countLabel: string;
  total: number | null | undefined;
  totalLabel: string;
  averageLabel: string;
  /** Renders the average; the caller owns the units. */
  format: (average: number) => ReactNode;
}>) {
  const sum = total ?? 0;
  return (
    <div className="fact-pair">
      <Fact value={count.toLocaleString('bg-BG')} label={countLabel} />
      <Fact value={<Amount value={sum} />} label={totalLabel} />
      <div className="fact fact-avg">
        <span className="fact-value">{count > 0 ? format(sum / count) : '—'}</span>
        <span className="fact-label">{averageLabel}</span>
      </div>
    </div>
  );
}

/**
 * A section the reader can fold away — and that folds itself away when
 * there is nothing inside it.
 *
 * A dossier stacks a company's whole public-funds record, and most
 * companies have most of it empty. An empty embedded list is not a blank
 * line: it is a heading, a search box, a full facet sidebar and a row
 * saying there are no results — a screen and a half of chrome standing
 * in for the word "none". Several of those in a row and the sections that
 * do hold something are below the fold.
 *
 * Collapsed is not hidden. The heading stays, with its count beside it,
 * so "0 общо" is still stated plainly — the reader learns there is
 * nothing here without scrolling past the apparatus for showing it, and
 * can still open it to see the filters for themselves. Absent sections
 * would instead leave them guessing whether we track this at all.
 *
 * Contents are mounted on first open and kept mounted afterwards. A
 * closed section therefore issues no queries — which is the other half
 * of the cost: the empty tender list on an ordinary company page was
 * fetching rows and every facet's counts to render the word "none".
 */
export function Disclosure({ title, aside, defaultOpen = true, children }: Readonly<{
  title: string;
  aside?: ReactNode;
  /** Open on arrival. Callers pass whether the section has anything in it. */
  defaultOpen?: boolean;
  children: ReactNode;
}>) {
  const [open, setOpen] = useState(defaultOpen);
  const [mounted, setMounted] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <section className="block">
      {/* The button sits inside the heading rather than around it: a
          heading is flow content and not valid inside a button, and
          screen readers need the level to navigate the dossier. */}
      <h2 className="section-title-folding">
        <button
          type="button"
          className="section-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => { setMounted(true); setOpen((was) => !was); }}
        >
          <span className="section-title section-toggle-text">{title}</span>
          {aside && <span className="muted section-toggle-aside">{aside}</span>}
          <span className="fct-toggle" aria-hidden="true">{open ? '−' : '+'}</span>
        </button>
      </h2>
      <div id={bodyId} hidden={!open}>{mounted && children}</div>
    </section>
  );
}

export function Breadcrumb({ children }: { children: ReactNode[] }) {
  return (
    <nav className="breadcrumb">
      {children.map((part, index) => (
        <span key={index}>{index > 0 && ' / '}{part}</span>
      ))}
    </nav>
  );
}

/* Widths that vary per row, because a stack of identical bars reads as a
   graphic and a ragged one reads as text about to arrive. Fixed rather
   than random so the shape does not change between renders. */
const SK_WIDTHS = [
  ['34%', '22%', '14%'],
  ['46%', '18%', '20%'],
  ['28%', '30%', '12%'],
  ['52%', '16%', '18%'],
  ['38%', '26%', '15%'],
  ['30%', '20%', '22%'],
  ['44%', '24%', '13%'],
  ['36%', '19%', '17%'],
];

/**
 * A waiting state shaped like the thing being waited for.
 *
 * The page keeps its proportions while it fills, so the content does not
 * jump when it lands, and the shape itself says what is coming — a table,
 * a row of figures — which a centred "Зареждане…" never did.
 *
 * The label is still rendered, out of view, because the animation is only
 * visible to someone looking at it.
 */
export function Loading({ label = 'Зареждане…', variant = 'table', rows = 6 }: {
  label?: string; variant?: 'table' | 'cards' | 'text'; rows?: number;
}) {
  const count = Math.max(1, Math.min(rows, SK_WIDTHS.length));
  return (
    <div className="sk" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {variant === 'cards' ? (
        <div className="sk-cards" aria-hidden="true">
          {Array.from({ length: count }, (_, index) => (
            <div className="sk-card" key={index}>
              <span className="skeleton sk-value" />
              <span className="skeleton sk-label" />
            </div>
          ))}
        </div>
      ) : (
        <div className="sk" aria-hidden="true">
          {variant === 'table' && (
            <div className="sk-row sk-head">
              {SK_WIDTHS[0]!.map((width, index) => (
                <span className="skeleton sk-bar" key={index} style={{ maxWidth: width }} />
              ))}
            </div>
          )}
          {Array.from({ length: count }, (_, row) => (
            <div className="sk-row" key={row}>
              {SK_WIDTHS[row % SK_WIDTHS.length]!.map((width, index) => (
                <span
                  className="skeleton sk-bar"
                  key={index}
                  style={{ maxWidth: variant === 'text' && index ? '0' : width }}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Data already on screen while a newer answer is on its way.
 *
 * Several lists keep the previous page's rows during a refetch, which
 * otherwise looks like a filter that did nothing. Blurring says the rows
 * are the old answer, and turning off pointer events stops a reader
 * opening a record that is about to be replaced by a different one.
 */
export function Refreshing({ busy, label = 'Обновяване…', children }: Readonly<{
  busy: boolean; label?: string; children: ReactNode;
}>) {
  return (
    <div className={busy ? 'refreshing' : undefined} aria-busy={busy || undefined}>
      {busy && (
        <span className="refreshing-note" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          {label}
        </span>
      )}
      <div className="refreshing-body">{children}</div>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return <div className="state state-error">Грешка: {message}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function GraphButton({ href, children = 'Виж в граф' }: {
  href: string; children?: ReactNode;
}) {
  return <a className="btn btn-primary" href={href}>{children}</a>;
}
